/*  Meet Ivory - /cart route behaviour (Whop/Lasso checkout migration).

    mrk.js deliberately refuses to build the cart drawer on the cart template:
    HeaderSection.prototype.initDrawers skips `new theme.CartDrawer()` whenever
    <body> carries `template-cart`, which is exactly what leaves the generic full
    cart page from sections/main-cart.liquid on screen.

    sections/main-cart.liquid loads this file on that template only, and it:
      1. builds the one existing #CartDrawer and opens it - for every visitor
         alike, whether they arrived by direct URL, from storefront navigation,
         or from the Lasso checkout logo (Lasso hardcodes that logo to
         `${storeWebsite}/cart`, and that now lands on the drawer like any other
         route rather than being bounced to the homepage);
      2. leaves /cart whenever the drawer is closed, so the full cart page
         underneath is never revealed.

    Failure-safe by construction. The markup hides nothing on its own: the full
    cart page is visible by default, and is hidden only while this file is proven
    to be running, through the `cart-drawer-route-active` class that this script -
    not the markup - puts on <html>. If the asset 404s, is blocked, or throws at
    any point, the class is never added or is taken off again, and the visitor
    gets the ordinary cart page instead of a blank screen. Nothing else can hide
    it, so there is no state in which /cart is empty with no way back.

    The drawer markup itself is untouched - this reuses snippets/cart-drawer.liquid
    exactly as the header rendered it. Every other template still gets its drawer
    from mrk.js, unchanged.
*/

(function() {
  'use strict';

  var OPEN_CLASS = 'drawer--is-open';
  var ACTIVE_CLASS = 'cart-drawer-route-active';
  var READY_TIMEOUT = 10000;
  var READY_INTERVAL = 50;

  var root = document.documentElement;

  // The section tag is synchronous and sits above the cart markup, so this lands
  // before that markup is parsed - hidden from the first paint, never a flash.
  function hideCartPage() {
    root.classList.add(ACTIVE_CLASS);
  }

  function revealCartPage() {
    root.classList.remove(ACTIVE_CLASS);
  }

  // Keep the theme preview session pinned, so testing the draft theme does not
  // quietly hand us back to the live theme on the way home.
  function homeUrl() {
    var preview = null;

    try {
      preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    } catch (err) {
      preview = null;
    }

    return preview ? '/?preview_theme_id=' + encodeURIComponent(preview) : '/';
  }

  /*  Leaving the drawer on /cart has to mean leaving /cart, however it was
      dismissed - close button, Escape, or a click outside.

      Watching the open class rather than wrapping theme.Drawers.close() is
      deliberate: Drawers.init() binds the close button with `this.close.bind(this)`,
      so it captures the original method and any wrapper installed afterwards is
      simply skipped by that button. Every close path ends in this one class being
      removed, so this catches all of them and needs no patching. */
  function goHomeWhenClosed(drawerEl) {
    var hasOpened = false;

    var observer = new MutationObserver(function() {
      if (drawerEl.classList.contains(OPEN_CLASS)) {
        hasOpened = true;
        return;
      }

      if (hasOpened) {
        observer.disconnect();
        window.location.href = homeUrl();
      }
    });

    observer.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
  }

  function buildDrawer() {
    if (theme.cartDrawerOnCartPage) {
      return;
    }

    var cartDrawer = new theme.CartDrawer();
    theme.cartDrawerOnCartPage = cartDrawer;

    goHomeWhenClosed(document.getElementById('CartDrawer'));
    cartDrawer.open();
  }

  function whenDrawerReady(callback) {
    var startedAt = Date.now();

    (function poll() {
      if (window.theme && typeof theme.CartDrawer === 'function' && document.getElementById('CartDrawerForm')) {
        try {
          callback();
        } catch (err) {
          // A drawer that cannot be built must not cost the visitor their cart.
          revealCartPage();
        }
        return;
      }

      if (Date.now() - startedAt < READY_TIMEOUT) {
        window.setTimeout(poll, READY_INTERVAL);
      } else {
        revealCartPage();
      }
    })();
  }

  function start() {
    whenDrawerReady(buildDrawer);
  }

  try {
    hideCartPage();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        try {
          start();
        } catch (err) {
          revealCartPage();
        }
      });
    } else {
      start();
    }
  } catch (err) {
    revealCartPage();
  }
})();
